import React, { Children } from 'react';
import { LogIn, Shield, MessageCircle, Calendar, Package, Star, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
interface ProfileGuestProps {
  onSignIn: () => void;
}
export function ProfileGuest({
  onSignIn
}: ProfileGuestProps) {
  const benefits = [{
    icon: Calendar,
    title: 'Book Items',
    description: 'Request rentals and manage your bookings'
  }, {
    icon: Package,
    title: 'List Your Items',
    description: 'Earn money by sharing what you own'
  }, {
    icon: MessageCircle,
    title: 'Chat with Owners',
    description: 'Ask questions before you book'
  }, {
    icon: Shield,
    title: 'Secure Transactions',
    description: 'Protected payments and deposits'
  }, {
    icon: Star,
    title: 'Build Your Reputation',
    description: 'Get reviews and verified badges'
  }, {
    icon: TrendingUp,
    title: 'Track Your Activity',
    description: 'View rental history and earnings'
  }];
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1489">
      {/* Header */}
      <div className="bg-white border-b border-sand" data-id="element-1490">
        <div className="max-w-md mx-auto px-4 pt-4 pb-3" data-id="element-1491">
          <Logo variant="full" size="md" data-id="element-1492" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-8 space-y-8" data-id="element-1493">
        {/* Hero Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center space-y-4" data-id="element-1494">
          <div className="w-20 h-20 mx-auto bg-dark-green/10 rounded-full flex items-center justify-center mb-4" data-id="element-1495">
            <LogIn size={40} className="text-dark-green" data-id="element-1496" />
          </div>
          <h1 className="text-3xl font-bold text-charcoal" data-id="element-1497">Join AllHere</h1>
          <p className="text-lg text-moss-gray leading-relaxed" data-id="element-1498">
            Borrow what you need, share what you have, and connect with your
            community.
          </p>
        </motion.div>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.5
      }} data-id="element-1499">
          <Button variant="primary" fullWidth size="lg" onClick={onSignIn} className="h-14 text-lg" data-id="element-1500">
            Sign In or Create Account
          </Button>
        </motion.div>

        {/* Benefits Grid */}
        <div data-id="element-1501">
          <h2 className="text-xl font-bold text-charcoal mb-4 text-center" data-id="element-1502">
            What You Can Do
          </h2>
          <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 gap-4" data-id="element-1503">
            {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return <motion.div key={index} variants={item} className="bg-white rounded-ios-lg p-5 shadow-sm border border-sand/50" data-id="element-1504">
                  <div className="w-12 h-12 bg-dark-green/10 rounded-full flex items-center justify-center mb-3" data-id="element-1505">
                    <Icon size={24} className="text-dark-green" data-id="element-1506" />
                  </div>
                  <h3 className="font-semibold text-charcoal mb-1 text-sm" data-id="element-1507">
                    {"Borrow Items"}
                  </h3>
                  <p className="text-xs text-moss-gray leading-relaxed" data-id="element-1508">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Trust Indicators */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.5
      }} className="bg-white rounded-ios-lg p-6 shadow-sm border border-sand/50" data-id="element-1509">
          <h3 className="font-semibold text-charcoal mb-4 text-center" data-id="element-1510">
            Safe & Secure
          </h3>
          <div className="space-y-3" data-id="element-1511">
            <div className="flex items-start gap-3" data-id="element-1512">
              <Shield size={20} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-1513" />
              <div data-id="element-1514">
                <p className="text-sm font-medium text-charcoal" data-id="element-1515">
                  Verified Users
                </p>
                <p className="text-xs text-moss-gray" data-id="element-1516">
                  Phone and ID verification for all members
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-1517">
              <Shield size={20} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-1518" />
              <div data-id="element-1519">
                <p className="text-sm font-medium text-charcoal" data-id="element-1520">
                  Protected Payments
                </p>
                <p className="text-xs text-moss-gray" data-id="element-1521">
                  Secure deposits and payment processing
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-1522">
              <Shield size={20} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-1523" />
              <div data-id="element-1524">
                <p className="text-sm font-medium text-charcoal" data-id="element-1525">
                  Community Reviews
                </p>
                <p className="text-xs text-moss-gray" data-id="element-1526">
                  Transparent ratings and feedback system
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} className="text-center pt-4" data-id="element-1527">
          <p className="text-sm text-moss-gray mb-4" data-id="element-1528">Ready to get started?</p>
          <Button variant="secondary" fullWidth size="lg" onClick={onSignIn} className="h-14" data-id="element-1529">
            Create Your Free Account
          </Button>
        </motion.div>
      </div>
    </div>;
}